import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from 'react-bootstrap/Button';
import CustomButton from '../../../component/CustomButton';
import momenttz from '../../../component/helpers/momenttz';
import { eventTypeDetection, eventStatusFormatter, getAccessibleSnapshotChannel } from '../../../component/helpers/helperEvents';

import './EventsTable.sass';

const EventsTable = (props) => {
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'homePage.vehicleSidebar.eventsTable' });
	const {
		events,
		page,
		showSnapshot,
		openClip,
		openEventSnapshot,
		sendRequestContextVideo,
		fetchMoreEvents,
		hasMoreEvents,
		loadingMore
	} = props;

	const user = useSelector(state => state.user.user);
	const tableRef = useRef(null);
	const lastPageRequested = useRef(null);

	const safeFetchMoreEvents = () => {
		const nextPage = page + 1;
		if (lastPageRequested.current === nextPage) return;
		lastPageRequested.current = nextPage;
		fetchMoreEvents();
	};

	const handleScroll = useCallback(() => {
		const el = tableRef.current;
		if (!el || loadingMore || !hasMoreEvents) return;

		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
			safeFetchMoreEvents();
		}
	}, [loadingMore, hasMoreEvents, safeFetchMoreEvents]);


	useEffect(() => {
		const el = tableRef.current;
		if (!el) return;
		el.addEventListener('scroll', handleScroll);
		return () => el.removeEventListener('scroll', handleScroll);
	}, [handleScroll]);

	useEffect(() => {
		if (page === 1) {
			lastPageRequested.current = null;
		}
	}, [page]);

	useEffect(() => {
		const el = tableRef.current;
		if (!el) return;

		const tryLoadMore = () => {
			if (hasMoreEvents && el.scrollHeight <= el.clientHeight && !loadingMore) {
				safeFetchMoreEvents();
			}
		};

		const timeout = setTimeout(tryLoadMore, 300);
		return () => clearTimeout(timeout);
	}, [page, hasMoreEvents, loadingMore]);

	const timestampFormatter = (cell, row) => {
		if (cell) {
			return momenttz(`${cell}+00`).format('h:mm a');
		}
		return t('notReviewed');
	}

	const typeFormatter = (cell, row) => {
		const type = eventTypeDetection(row)
		return i18n.t(`constants.eventNames.${type.replace(/\s/g, "_")}`, type);
	}

	const videoOptionFormatter = (cell, row) => {
		if (row.video_option === 'alert_only') {
			return (
				<CustomButton variant="link" title={t('titleBtnRequest')} onClick={() => sendRequestContextVideo(row)}>{t('btnRequest')}</CustomButton>
			)
		}
		if (row.video_option === 'video_snapshot' || (row.type === 'Custom Clip' && row.event_type !== 'SNAPSHOTS_CLIP') || row.uploading_status === 'VIDEO_UPLOADED' || row.uploading_status === 'PARTIAL_VIDEO') {
			if (row.uploading_status === 'VIDEO_UPLOADED' || row.uploading_status === 'PARTIAL_VIDEO') {
				return (
					<CustomButton variant="link" title={t('titleBtnView')} onClick={(e) => openClip(e, row)}>{t('btnView')}</CustomButton>
				)
			} else {
				return i18n.t(`homePage.vehicleSidebar.vehicleSidebarFilter.${eventStatusFormatter(row.uploading_status)?.replace(/\s/g, "_")}`)
			}
		}
		if (row.video_option === 'snapshot_only' || row.type === 'Custom Clip') {
			if (row.snapshot_status === 'SNAPSHOT_UPLOADED') {
				return (
					<CustomButton variant="link" title={t('titleBtnPending')} onClick={() => openEventSnapshot(row)}>{t('btnView')}</CustomButton>
				)
			} else {
				// return i18n.t(`homePage.vehicleSidebar.vehicleSidebarFilter.${eventStatusFormatter(row.uploading_status)?.replace(/\s/g, "_")}`)
				return i18n.t(`homePage.vehicleSidebar.vehicleSidebarFilter.${eventStatusFormatter(row.snapshot_status)?.replace(/\s/g, "_")}`)
			}
		}

	}

	return (
		<div className="vehicleSidebarTable" ref={tableRef}>
			{
				events.map((item) => {
					const itemSnapshot = item[`snapshot_${getAccessibleSnapshotChannel(item, user, [1, 2])}`];
					return (
						<div className="event-wrapper" key={item.id}>
							<div className="event-row">
								<div className="timestamp">{timestampFormatter(item.timestamp, item)}</div>
								<div className="event-type">{typeFormatter(item.type, item)}</div>
								<div className="video-option">{videoOptionFormatter(item.video_option, item)}</div>
							</div>
							{showSnapshot && itemSnapshot &&
								<div className="event-snapshot">
									<img src={itemSnapshot} />
								</div>
							}
						</div>
					)
				})
			}
			{loadingMore && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						borderTop: '1px solid #ddd',
						padding: '10px 0',
					}}
				>
					<Button variant="link" size="sm" style={{ textDecoration: 'none' }} disabled>
						{t('loading')}
					</Button>
				</div>
			)}
		</div>
	)
}

EventsTable.propTypes = {
	events: PropTypes.arrayOf(PropTypes.any).isRequired,
	showSnapshot: PropTypes.bool.isRequired,
	openClip: PropTypes.func.isRequired,
	openEventSnapshot: PropTypes.func.isRequired,
	sendRequestContextVideo: PropTypes.func.isRequired,
}

export default EventsTable;
